/**
 * Buy credits (mission 9 WP13 T081; sheet 50 callout 8; B.4, B.3; FR-006; OR-17, C17, U30;
 * contracts/ui-api.md "Sheet 50", "The refused surfaces"; research §1.15).
 *
 * - The tiers are the billing read's `tiers`, a radiogroup of native radios (`buyTier`: the size, the
 *   price and the price per 1,000, every figure an argument; C-010).
 * - Buy opens a confirmation naming the card (`buyConfirmBody` with `cardValue`). An opening draws a
 *   `request_id`; every retry sends the same one, so a retried charge is one purchase (OR-17, C17). It
 *   survives a close while the outcome is unknown (pending, a network error, a 5xx), so buying again
 *   replays it. It is kept in the tab's `sessionStorage` too (`lib/purchase-request`), so a reload or
 *   remount replays it as well (audit A25a). A success or a definite 4xx refusal draws a new one.
 * - `done`: `buyDone`, and the purchase mutation writes the balance into `['billing']` and reads the
 *   credits and estimates again (WP11).
 * - A refusal stays in the confirmation with its register row: the 402 `payment-failed` (`declined`),
 *   the 409 `request-reused`, a 503.
 * - `no-card`: `buyNoCard`, and Buy is disabled with it as the reason.
 * - `read-only-disabled`: refused through `useBillingGate`; the gated primary is demoted to secondary and
 *   relabeled `gatedReadOnly`, the reason in its tooltip (§7.1, §11; U30).
 */
import { useId, useRef, useState } from 'react'

import { Band } from '@/components/trellis/Band'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { BillingView, CreditTier } from '@/lib/api/billing'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { money } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { isDefiniteRefusal, pendingPurchaseOr, storePendingPurchase } from '@/lib/purchase-request'
import { usePurchaseMutation } from '@/lib/queries'

import { BillingProblem } from './BillingProblem'

/** The unit of a tier's `cents_per_1000` (billing-api `tiers`). */
const TIER_UNIT = 1000

function tierLabel(tier: CreditTier): string {
  return PROVISIONAL_COPY.buyTier.render({
    credits: tier.credits,
    price: money(tier.price_cents),
    per: money(tier.cents_per_1000),
    unit: TIER_UNIT,
  })
}

export function BuyCredits({ view }: { view: BillingView }) {
  const gate = useBillingGate()
  const purchase = usePurchaseMutation()
  const titleId = useId()
  const bodyId = useId()
  const [selected, setSelected] = useState(view.tiers[0]?.credits ?? null)
  const [requestId, setRequestId] = useState<string | null>(null)
  const [opened, setOpened] = useState(false)
  const [bought, setBought] = useState<number | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const tier = view.tiers.find((t) => t.credits === selected)
  const { card } = view

  function keep(id: string | null) {
    setRequestId(id)
    if (id !== null) storePendingPurchase(id) // the mutation clears a settled id (usePurchaseMutation)
  }

  function open() {
    purchase.reset()
    setBought(null)
    keep(pendingPurchaseOr(requestId))
    setOpened(true)
  }

  function close() {
    setOpened(false)
  }

  function confirm() {
    if (tier === undefined || requestId === null) return
    purchase.mutate(
      { credits: tier.credits, request_id: requestId },
      {
        onSuccess: (result) => {
          setBought(result.block.credits)
          keep(null)
          setOpened(false)
        },
        onError: (error) => {
          if (isDefiniteRefusal(error)) {
            keep(crypto.randomUUID())
          }
        },
      },
    )
  }

  let buy
  if (gate.refused) {
    buy = (
      <Button variant="secondary" {...gateButton(gate)}>
        {copyText('gatedReadOnly')}
      </Button>
    )
  } else if (card === null) {
    buy = (
      <Button disabled disabledReason={copyText('buyNoCard')}>
        {copyText('buyAction')}
      </Button>
    )
  } else {
    buy = (
      <Button ref={triggerRef} onClick={open}>
        {copyText('buyAction')}
      </Button>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle id={titleId}>{copyText('buyTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div role="radiogroup" aria-labelledby={titleId} className="flex flex-col gap-2">
          {view.tiers.map((t) => (
            <label key={t.credits} className="flex items-center gap-2 text-body text-navy-950">
              <input
                type="radio"
                name="credit-tier"
                className="size-[18px] accent-navy-950"
                checked={selected === t.credits}
                onChange={() => setSelected(t.credits)}
              />
              {tierLabel(t)}
            </label>
          ))}
        </div>
        {card === null ? <p className="text-body-sm text-navy-600">{copyText('buyNoCard')}</p> : null}
        {bought === null ? null : (
          <Band kind="success" requirement="B.4">
            <p>{PROVISIONAL_COPY.buyDone.render({ credits: bought })}</p>
          </Band>
        )}
        <div>{buy}</div>
      </CardContent>
      <Dialog open={opened} onOpenChange={(next) => (next ? undefined : close())}>
        <DialogContent
          size="sm"
          aria-describedby={bodyId}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            triggerRef.current?.focus()
          }}
        >
          <DialogHeader>
            <DialogTitle>{copyText('buyTitle')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <DialogDescription id={bodyId}>
              {tier === undefined || card === null
                ? null
                : PROVISIONAL_COPY.buyConfirmBody.render({
                    price: money(tier.price_cents),
                    card: PROVISIONAL_COPY.cardValue.render(card),
                    credits: tier.credits,
                  })}
            </DialogDescription>
            {purchase.error === null ? null : <BillingProblem error={purchase.error} />}
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">{copyText('modalCancel')}</Button>
            </DialogClose>
            <Button loading={purchase.isPending} onClick={confirm}>
              {copyText('buyAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
